import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Checkbox,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  Grid,
  InputAdornment,
  InputLabel,
  LinearProgress,
  Menu,
  MenuItem,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Tooltip,
  Typography
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import ClearIcon from '@mui/icons-material/Clear';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import DeleteIcon from '@mui/icons-material/Delete';
import ViewColumnIcon from '@mui/icons-material/ViewColumn';
import DescriptionIcon from '@mui/icons-material/Description';
import {
  marketplaceApi,
  formatDuration,
  formatMarketplaceUploadError,
  MarketplaceFilterOptions,
  MarketplaceImportJob,
  MarketplaceListParams,
  MarketplaceRecord,
  MarketplaceSource,
  MARKETPLACE_LABELS,
  marketplaceLabel
} from '../api/marketplace.api';
import { markDismissed } from '../components/MarketplaceImportToast';

interface MarketplaceDataProps {
  source: MarketplaceSource;
}

type ColumnKey =
  | 'website'
  | 'websiteUrl'
  | 'da'
  | 'dr'
  | 'traffic'
  | 'keywords'
  | 'language'
  | 'price'
  | 'country'
  | 'categories'
  | 'referringDomains'
  | 'outgoingDomains'
  | 'authorityScore'
  | 'trustFlow'
  | 'spamScore'
  | 'turnaroundTime'
  | 'linkType'
  | 'sponsored'
  | 'homepage';

const ALL_COLUMNS: Array<{ key: ColumnKey; label: string; defaultVisible: boolean }> = [
  { key: 'websiteUrl', label: 'Website URL', defaultVisible: true },
  { key: 'dr', label: 'DR', defaultVisible: true },
  { key: 'traffic', label: 'Traffic', defaultVisible: true },
  { key: 'keywords', label: 'Keyword', defaultVisible: true },
  { key: 'language', label: 'Language', defaultVisible: true },
  { key: 'price', label: 'Price', defaultVisible: true },
  { key: 'website', label: 'Website', defaultVisible: false },
  { key: 'da', label: 'DA', defaultVisible: false },
  { key: 'country', label: 'Country', defaultVisible: false },
  { key: 'categories', label: 'Categories', defaultVisible: false },
  { key: 'referringDomains', label: 'Ref Domains', defaultVisible: false },
  { key: 'outgoingDomains', label: 'Out Domains', defaultVisible: false },
  { key: 'authorityScore', label: 'AS', defaultVisible: false },
  { key: 'trustFlow', label: 'TF', defaultVisible: false },
  { key: 'spamScore', label: 'Spam', defaultVisible: false },
  { key: 'turnaroundTime', label: 'TAT', defaultVisible: false },
  { key: 'linkType', label: 'Link Type', defaultVisible: false },
  { key: 'sponsored', label: 'Sponsored', defaultVisible: false },
  { key: 'homepage', label: 'Homepage', defaultVisible: false }
];

const DEFAULT_VISIBLE = ALL_COLUMNS.filter((c) => c.defaultVisible).map((c) => c.key);

function formatCell(row: MarketplaceRecord, key: ColumnKey): React.ReactNode {
  const value = row[key as keyof MarketplaceRecord];
  if (value === null || value === undefined || value === '') return '—';
  if (key === 'price') {
    return `$${value}`;
  }
  if (key === 'websiteUrl') {
    const raw = String(value).trim();
    const href = /^https?:\/\//i.test(raw) ? raw : `https://${raw}`;
    return (
      <Tooltip title={raw}>
        <Typography
          component="a"
          href={href}
          target="_blank"
          rel="noopener noreferrer"
          variant="body2"
          noWrap
          sx={{ maxWidth: 200, display: 'inline-block', color: 'primary.main', textDecoration: 'none', '&:hover': { textDecoration: 'underline' } }}
        >
          {raw}
        </Typography>
      </Tooltip>
    );
  }
  if (key === 'website' || key === 'categories') {
    return (
      <Tooltip title={String(value)}>
        <Typography variant="body2" noWrap sx={{ maxWidth: key === 'categories' ? 140 : 200 }}>
          {String(value)}
        </Typography>
      </Tooltip>
    );
  }
  return String(value);
}

const emptyFilters: MarketplaceListParams = {
  search: '',
  language: '',
  country: '',
  categories: '',
  linkType: '',
  sponsored: '',
  daMin: '',
  daMax: '',
  drMin: '',
  drMax: '',
  trafficMin: '',
  trafficMax: '',
  priceMin: '',
  priceMax: ''
};

const MarketplaceData: React.FC<MarketplaceDataProps> = ({ source }) => {
  const navigate = useNavigate();
  const builtinLabel = MARKETPLACE_LABELS[source];
  const [customLabel, setCustomLabel] = useState(source);
  const [renameOpen, setRenameOpen] = useState(false);
  const [renameName, setRenameName] = useState('');
  const [renameError, setRenameError] = useState('');
  const [renaming, setRenaming] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [deletingMarketplace, setDeletingMarketplace] = useState(false);
  useEffect(() => {
    if (builtinLabel) return;
    let cancelled = false;
    marketplaceApi.listCatalog()
      .then((items) => {
        if (cancelled) return;
        const found = items.find((item) => item.slug === source);
        setCustomLabel(found?.name || source);
      })
      .catch(() => {
        if (!cancelled) setCustomLabel(source);
      });
    return () => {
      cancelled = true;
    };
  }, [source, builtinLabel]);

  useEffect(() => {
    if (builtinLabel) return;
    const refreshName = () => {
      marketplaceApi.listCatalog()
        .then((items) => {
          const found = items.find((item) => item.slug === source);
          setCustomLabel(found?.name || source);
        })
        .catch(() => undefined);
    };
    window.addEventListener('marketplace-catalog-changed', refreshName);
    return () => window.removeEventListener('marketplace-catalog-changed', refreshName);
  }, [source, builtinLabel]);

  const label = builtinLabel || customLabel;

  const saveRename = async () => {
    const name = renameName.trim();
    if (name.length < 2) {
      setRenameError('Enter a name with at least 2 characters');
      return;
    }
    setRenaming(true);
    setRenameError('');
    try {
      const updated = await marketplaceApi.renameCatalog(source, name);
      setCustomLabel(updated.name);
      setRenameOpen(false);
      setSuccess(`Marketplace renamed to "${updated.name}".`);
      window.dispatchEvent(new Event('marketplace-catalog-changed'));
    } catch (err: any) {
      setRenameError(
        err?.response?.data?.error?.message ||
          err?.response?.data?.message ||
          'Could not rename marketplace'
      );
    } finally {
      setRenaming(false);
    }
  };

  const deleteMarketplace = async () => {
    setDeletingMarketplace(true);
    try {
      const removedName = label;
      await marketplaceApi.deleteCatalog(source);
      window.dispatchEvent(new CustomEvent('marketplace-catalog-notice', {
        detail: { message: `"${removedName}" and its sites were deleted.`, severity: 'success' }
      }));
      window.dispatchEvent(new Event('marketplace-catalog-changed'));
      setDeleteOpen(false);
      navigate('/marketplace/link-publisher');
    } catch (err: any) {
      setError(
        err?.response?.data?.error?.message ||
          err?.response?.data?.message ||
          'Could not delete marketplace'
      );
    } finally {
      setDeletingMarketplace(false);
    }
  };
  const [data, setData] = useState<MarketplaceRecord[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(50);
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [exportProgress, setExportProgress] = useState<{
    mode: 'selected' | 'all';
    expectedRows: number;
    phase: string;
    startedAt: number;
    percent: number;
  } | null>(null);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [filters, setFilters] = useState<MarketplaceListParams>({ ...emptyFilters });
  const [appliedFilters, setAppliedFilters] = useState<MarketplaceListParams>({ ...emptyFilters });
  const [filterOptions, setFilterOptions] = useState<MarketplaceFilterOptions>({
    languages: [],
    countries: [],
    linkTypes: [],
    sponsored: []
  });
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [visibleColumns, setVisibleColumns] = useState<ColumnKey[]>([...DEFAULT_VISIBLE]);
  const [columnsAnchor, setColumnsAnchor] = useState<null | HTMLElement>(null);
  const [importJobs, setImportJobs] = useState<MarketplaceImportJob[]>([]);
  const [tick, setTick] = useState(0);
  const fileInputRef = React.useRef<HTMLInputElement>(null);

  const visibleColumnDefs = useMemo(
    () => ALL_COLUMNS.filter((c) => visibleColumns.includes(c.key)),
    [visibleColumns]
  );

  // Progress banner: only in-progress imports for this module
  const processingJobs = importJobs.filter((j) => j.status === 'PROCESSING' && j.source === source);
  const anyImportRunning = processingJobs.length > 0;

  const refreshJobs = useCallback(async () => {
    try {
      const jobs = await marketplaceApi.getActiveJobs();
      return jobs.filter((j) => j.source === source);
    } catch {
      return [] as MarketplaceImportJob[];
    }
  }, [source]);

  // Keep a live clock for elapsed display while imports or exports run
  useEffect(() => {
    if (!anyImportRunning && !exporting) return;
    const id = setInterval(() => setTick((t) => t + 1), 1000);
    return () => clearInterval(id);
  }, [anyImportRunning, exporting]);

  // Auto-hide local success (upload started, etc.) after a few seconds
  useEffect(() => {
    if (!success) return;
    const id = setTimeout(() => setSuccess(''), 6000);
    return () => clearTimeout(id);
  }, [success]);

  useEffect(() => {
    if (!error) return;
    const id = setTimeout(() => setError(''), 15000);
    return () => clearTimeout(id);
  }, [error]);

  const toggleColumn = (key: ColumnKey) => {
    setVisibleColumns((prev) => {
      if (prev.includes(key)) {
        if (prev.length <= 1) return prev;
        return prev.filter((k) => k !== key);
      }
      return [...prev, key];
    });
  };

  const loadFilterOptions = useCallback(async () => {
    try {
      const options = await marketplaceApi.filterOptions(source);
      setFilterOptions(options);
    } catch {
      // ignore — options populate after data exists
    }
  }, [source]);

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      setError('');
      const result = await marketplaceApi.list(source, {
        ...appliedFilters,
        page: page + 1,
        limit: rowsPerPage,
        sortBy: 'createdAt',
        sortOrder: 'desc'
      });
      setData(result.data || []);
      setTotal(result.pagination?.total || 0);
    } catch (err: any) {
      setError(formatMarketplaceUploadError(err, 'Failed to load marketplace data'));
    } finally {
      setLoading(false);
    }
  }, [source, appliedFilters, page, rowsPerPage]);

  // Poll progress for this module; completion toast is global (bottom of any page)
  useEffect(() => {
    let cancelled = false;
    const poll = async () => {
      const jobs = await refreshJobs();
      if (cancelled) return;
      setImportJobs(jobs.filter((j) => j.status === 'PROCESSING'));
    };
    poll();
    const id = setInterval(poll, 1500);
    return () => {
      cancelled = true;
      clearInterval(id);
    };
  }, [refreshJobs]);

  // Refresh table / show clear error when import finishes for this module
  useEffect(() => {
    const onFinished = (event: Event) => {
      const detail = (event as CustomEvent).detail as { job?: MarketplaceImportJob };
      const job = detail?.job;
      if (!job || job.source !== source) return;
      if (job.status === 'COMPLETED') {
        loadFilterOptions();
        fetchData();
      } else if (job.status === 'FAILED') {
        setError(
          formatMarketplaceUploadError(
            { message: job.error },
            `Import failed for ${job.fileName}. Check the CSV format and try again.`
          )
        );
      }
    };
    window.addEventListener('marketplace-import-finished', onFinished);
    return () => window.removeEventListener('marketplace-import-finished', onFinished);
  }, [source, loadFilterOptions, fetchData]);

  useEffect(() => {
    setPage(0);
    setSelectedIds(new Set());
    setFilters({ ...emptyFilters });
    setAppliedFilters({ ...emptyFilters });
    setData([]);
    setTotal(0);
    loadFilterOptions();
  }, [source, loadFilterOptions]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Live filter: apply as user types / changes any filter input
  useEffect(() => {
    const timer = setTimeout(() => {
      setAppliedFilters((prev) => {
        if (JSON.stringify(prev) === JSON.stringify(filters)) return prev;
        setPage(0);
        return { ...filters };
      });
    }, 350);
    return () => clearTimeout(timer);
  }, [filters]);

  const clearFilters = () => {
    const cleared = { ...emptyFilters };
    setFilters(cleared);
    setAppliedFilters(cleared);
    setPage(0);
  };

  const toggleSelectAll = () => {
    if (selectedIds.size === data.length) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(data.map((r) => r.id)));
    }
  };

  const toggleSelect = (id: string) => {
    const next = new Set(selectedIds);
    if (next.has(id)) next.delete(id);
    else next.add(id);
    setSelectedIds(next);
  };

  const handleUpload = async (file: File) => {
    setUploading(true);
    setError('');
    setSuccess('');

    const name = (file.name || '').toLowerCase();
    if (!name.endsWith('.csv')) {
      setError('Only CSV files (.csv) are allowed. Please convert your file to CSV and upload again.');
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
      return;
    }

    if (file.size === 0) {
      setError('The selected file is empty. Please upload a CSV with headers and data rows.');
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
      return;
    }

    const maxBytes = 100 * 1024 * 1024; // 100MB — match backend MAX_FILE_SIZE
    if (file.size > maxBytes) {
      const sizeMb = (file.size / (1024 * 1024)).toFixed(1);
      setError(
        `File is too large (${sizeMb} MB). Maximum upload size is 100 MB. Please compress or split the CSV and try again.`
      );
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
      return;
    }

    try {
      const result = await marketplaceApi.upload(source, file);
      if (result.processing && result.data?.jobId) {
        setSuccess(
          `Import started for ${file.name}. You can leave this page — come back here to see progress.`
        );
        const jobs = await refreshJobs();
        setImportJobs(jobs.filter((j) => j.status === 'PROCESSING'));
      } else {
        setSuccess(result.message || `Imported into ${label}`);
        await loadFilterOptions();
        await fetchData();
      }
    } catch (err: any) {
      setError(formatMarketplaceUploadError(err, 'Upload failed. Check the CSV and try again.'));
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const handleCancelImport = async (jobId: string) => {
    try {
      await marketplaceApi.cancelJob(jobId);
      markDismissed(jobId);
      setImportJobs((prev) => prev.filter((j) => j.jobId !== jobId));
      setSuccess('Import cancel requested');
    } catch (err: any) {
      setError(err.response?.data?.message || err.message || 'Failed to cancel import');
    }
  };

  const downloadBlob = (blob: Blob, filename: string) => {
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', filename);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
  };

  const handleExportSelected = async () => {
    if (selectedIds.size === 0) {
      setError('Select at least one row to export');
      return;
    }
    const expectedRows = selectedIds.size;
    setExporting(true);
    setExportProgress({
      mode: 'selected',
      expectedRows,
      phase: 'Starting export…',
      startedAt: Date.now(),
      percent: 0
    });
    setError('');
    try {
      const { blob, count } = await marketplaceApi.export(
        source,
        { ids: Array.from(selectedIds) },
        (info) => {
          setExportProgress((prev) =>
            prev
              ? {
                  ...prev,
                  phase: info.phase,
                  percent: info.percent,
                  expectedRows: info.totalRows ?? prev.expectedRows
                }
              : prev
          );
        }
      );
      downloadBlob(blob, `${source}_selected_${new Date().toISOString().slice(0, 10)}.csv`);
      setSuccess(`Exported ${count || selectedIds.size} selected row(s)`);
    } catch (err: any) {
      setError(err.message || 'Export failed');
    } finally {
      setExporting(false);
      setExportProgress(null);
    }
  };

  const handleExportAll = async () => {
    setExporting(true);
    setExportProgress({
      mode: 'all',
      expectedRows: total,
      phase: 'Starting export…',
      startedAt: Date.now(),
      percent: 0
    });
    setError('');
    try {
      const { blob, count } = await marketplaceApi.export(
        source,
        { exportAll: true, ...appliedFilters },
        (info) => {
          setExportProgress((prev) =>
            prev
              ? {
                  ...prev,
                  phase: info.phase,
                  percent: info.percent,
                  expectedRows: info.totalRows ?? prev.expectedRows
                }
              : prev
          );
        }
      );
      downloadBlob(blob, `${source}_full_${new Date().toISOString().slice(0, 10)}.csv`);
      setSuccess(`Exported ${count || 'all'} row(s) (current filters applied)`);
    } catch (err: any) {
      setError(err.message || 'Export failed');
    } finally {
      setExporting(false);
      setExportProgress(null);
    }
  };

  const handleDownloadTemplate = async () => {
    try {
      setError('');
      const blob = await marketplaceApi.downloadTemplate(source);
      downloadBlob(blob, `${source}_import_template.csv`);
      setSuccess('Import template downloaded. Fill any columns you have — none are required.');
    } catch (err: any) {
      setError(err.message || 'Failed to download template');
    }
  };

  const handleBulkDelete = async () => {
    if (selectedIds.size === 0) return;
    if (!window.confirm(`Delete ${selectedIds.size} selected record(s) from ${label}?`)) return;
    try {
      await marketplaceApi.bulkDelete(source, Array.from(selectedIds));
      setSuccess(`Deleted ${selectedIds.size} record(s)`);
      setSelectedIds(new Set());
      await fetchData();
      await loadFilterOptions();
    } catch (err: any) {
      setError(err.response?.data?.message || err.message || 'Delete failed');
    }
  };

  const setFilter = (key: keyof MarketplaceListParams, value: string | number) => {
    setFilters((prev) => ({ ...prev, [key]: value }));
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2, flexWrap: 'wrap', gap: 1 }}>
        <Box>
          <Typography variant="h5" fontWeight="bold">
            Marketplace — {label}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Browse, import, and export marketplace listings. Use filters to refine results and manage your catalog with ease.
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
          {!builtinLabel && (
            <>
              <Button
                variant="outlined"
                onClick={() => {
                  setRenameName(label);
                  setRenameError('');
                  setRenameOpen(true);
                }}
              >
                Rename
              </Button>
              <Button variant="outlined" color="error" onClick={() => setDeleteOpen(true)}>
                Delete marketplace
              </Button>
            </>
          )}
          <input
            ref={fileInputRef}
            type="file"
            accept=".csv,text/csv"
            hidden
            onChange={(e) => {
              const file = e.target.files?.[0];
              if (file) handleUpload(file);
            }}
          />
          <Button
            variant="outlined"
            startIcon={<DescriptionIcon />}
            onClick={handleDownloadTemplate}
          >
            Download CSV Format
          </Button>
          <Button
            variant="contained"
            startIcon={uploading ? <CircularProgress size={16} color="inherit" /> : <UploadFileIcon />}
            disabled={uploading}
            onClick={() => fileInputRef.current?.click()}
          >
            Import CSV
          </Button>
          <Button
            variant="outlined"
            startIcon={
              exporting && exportProgress?.mode === 'selected' ? (
                <CircularProgress size={16} color="inherit" />
              ) : (
                <FileDownloadIcon />
              )
            }
            disabled={exporting || selectedIds.size === 0}
            onClick={handleExportSelected}
          >
            {exporting && exportProgress?.mode === 'selected'
              ? 'Exporting…'
              : `Export Selected (${selectedIds.size})`}
          </Button>
          <Button
            variant="outlined"
            startIcon={
              exporting && exportProgress?.mode === 'all' ? (
                <CircularProgress size={16} color="inherit" />
              ) : (
                <FileDownloadIcon />
              )
            }
            disabled={exporting || total === 0}
            onClick={handleExportAll}
          >
            {exporting && exportProgress?.mode === 'all' ? 'Exporting…' : 'Export All / Filtered'}
          </Button>
          {selectedIds.size > 0 && (
            <Button
              variant="outlined"
              color="error"
              startIcon={<DeleteIcon />}
              onClick={handleBulkDelete}
            >
              Delete ({selectedIds.size})
            </Button>
          )}
          <Button
            variant="outlined"
            startIcon={<ViewColumnIcon />}
            onClick={(e) => setColumnsAnchor(e.currentTarget)}
          >
            Columns
          </Button>
          <Menu
            anchorEl={columnsAnchor}
            open={Boolean(columnsAnchor)}
            onClose={() => setColumnsAnchor(null)}
            PaperProps={{ sx: { maxHeight: 420, px: 1 } }}
          >
            <MenuItem disabled dense>
              <Typography variant="caption" color="text.secondary">
                Show / hide columns
              </Typography>
            </MenuItem>
            {ALL_COLUMNS.map((col) => (
              <MenuItem key={col.key} dense onClick={() => toggleColumn(col.key)} sx={{ py: 0 }}>
                <Checkbox size="small" checked={visibleColumns.includes(col.key)} tabIndex={-1} disableRipple />
                <Typography variant="body2" sx={{ ml: 1 }}>
                  {col.label}
                </Typography>
              </MenuItem>
            ))}
            <MenuItem
              dense
              onClick={() => {
                setVisibleColumns([...DEFAULT_VISIBLE]);
                setColumnsAnchor(null);
              }}
            >
              Reset to default
            </MenuItem>
          </Menu>
        </Box>
      </Box>

      {(processingJobs.length > 0 || exportProgress) && (
        <Box sx={{ mb: 2 }}>
          {exportProgress && (
            <Alert
              severity="info"
              sx={{
                mb: 1,
                width: '100%',
                alignItems: 'flex-start',
                '& .MuiAlert-message': { width: '100%', flex: 1, minWidth: 0 }
              }}
            >
              <Box sx={{ width: '100%' }}>
                <Box
                  sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'baseline',
                    gap: 2,
                    mb: 1,
                    flexWrap: 'wrap'
                  }}
                >
                  <Typography variant="subtitle2">
                    {label} — Exporting{' '}
                    {exportProgress.mode === 'selected' ? 'selected rows' : 'all / filtered rows'}…
                  </Typography>
                  <Typography variant="subtitle2" fontWeight={700} color="primary.main">
                    {void tick}
                    {exportProgress.percent}%
                  </Typography>
                </Box>
                <LinearProgress
                  variant="determinate"
                  value={exportProgress.percent}
                  sx={{
                    width: '100%',
                    height: 10,
                    mb: 1.25,
                    borderRadius: 999,
                    bgcolor: 'action.hover',
                    '& .MuiLinearProgress-bar': { borderRadius: 999 }
                  }}
                />
                <Box
                  sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    gap: 2,
                    flexWrap: 'wrap'
                  }}
                >
                  <Typography variant="body2">
                    {exportProgress.phase}
                    {exportProgress.expectedRows > 0 &&
                      ` · ${exportProgress.expectedRows.toLocaleString()} rows`}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Elapsed: {formatDuration(Date.now() - exportProgress.startedAt)}
                  </Typography>
                </Box>
              </Box>
            </Alert>
          )}
          {processingJobs.map((job) => {
            const pct = job.progress?.percent ?? 0;
            const elapsed = Date.now() - (job.createdAt || Date.now());
            void tick;
            const eta = job.progress?.etaMs;
            return (
              <Alert
                key={job.jobId}
                severity="info"
                sx={{
                  mb: 1,
                  width: '100%',
                  alignItems: 'flex-start',
                  '& .MuiAlert-message': { width: '100%', flex: 1, minWidth: 0 }
                }}
                action={
                  <Button color="inherit" size="small" onClick={() => handleCancelImport(job.jobId)}>
                    Cancel
                  </Button>
                }
              >
                <Box sx={{ width: '100%' }}>
                  <Box
                    sx={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'baseline',
                      gap: 2,
                      mb: 1,
                      flexWrap: 'wrap'
                    }}
                  >
                    <Typography variant="subtitle2">
                      {marketplaceLabel(job.source)} — {job.fileName} (PROCESSING)
                    </Typography>
                    <Typography variant="subtitle2" fontWeight={700} color="primary.main">
                      {pct}%
                    </Typography>
                  </Box>
                  <LinearProgress
                    variant={pct > 0 ? 'determinate' : 'indeterminate'}
                    value={pct}
                    sx={{
                      width: '100%',
                      height: 10,
                      mb: 1.25,
                      borderRadius: 999,
                      bgcolor: 'action.hover',
                      '& .MuiLinearProgress-bar': { borderRadius: 999 }
                    }}
                  />
                  <Box
                    sx={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      gap: 2,
                      flexWrap: 'wrap'
                    }}
                  >
                    <Typography variant="body2">
                      {job.progress?.phase || '…'}
                      {(job.progress?.processedRows != null || job.progress?.totalRows != null) &&
                        ` · ${(job.progress?.processedRows || 0).toLocaleString()} / ${(job.progress?.totalRows || 0).toLocaleString()} rows`}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      Elapsed: {formatDuration(elapsed)}
                      {eta != null && eta > 0 ? ` · ETA: ~${formatDuration(eta)}` : ''}
                    </Typography>
                  </Box>
                </Box>
              </Alert>
            );
          })}
        </Box>
      )}

      {error && (
        <Alert severity="error" sx={{ mb: 2, whiteSpace: 'pre-line' }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}
      {success && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess('')}>
          {success}
        </Alert>
      )}

      <Card sx={{ mb: 2 }}>
        <CardContent>
          <Typography variant="subtitle2" sx={{ mb: 1.5 }}>
            Filters
          </Typography>
          <Grid container spacing={1.5}>
            <Grid item xs={12} md={3}>
              <TextField
                fullWidth
                size="small"
                label="Search website / URL"
                value={filters.search || ''}
                onChange={(e) => setFilter('search', e.target.value)}
                placeholder="Type to search…"
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon fontSize="small" />
                    </InputAdornment>
                  )
                }}
              />
            </Grid>
            <Grid item xs={6} md={2}>
              <FormControl fullWidth size="small">
                <InputLabel>Language</InputLabel>
                <Select
                  label="Language"
                  value={filters.language || ''}
                  onChange={(e) => setFilter('language', e.target.value)}
                >
                  <MenuItem value="">All</MenuItem>
                  {filterOptions.languages.map((v) => (
                    <MenuItem key={v} value={v}>
                      {v}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={6} md={2}>
              <FormControl fullWidth size="small">
                <InputLabel>Country</InputLabel>
                <Select
                  label="Country"
                  value={filters.country || ''}
                  onChange={(e) => setFilter('country', e.target.value)}
                >
                  <MenuItem value="">All</MenuItem>
                  {filterOptions.countries.map((v) => (
                    <MenuItem key={v} value={v}>
                      {v}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={6} md={2}>
              <FormControl fullWidth size="small">
                <InputLabel>Link Type</InputLabel>
                <Select
                  label="Link Type"
                  value={filters.linkType || ''}
                  onChange={(e) => setFilter('linkType', e.target.value)}
                >
                  <MenuItem value="">All</MenuItem>
                  {filterOptions.linkTypes.map((v) => (
                    <MenuItem key={v} value={v}>
                      {v}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={6} md={1.5}>
              <FormControl fullWidth size="small">
                <InputLabel>Sponsored</InputLabel>
                <Select
                  label="Sponsored"
                  value={filters.sponsored || ''}
                  onChange={(e) => setFilter('sponsored', e.target.value)}
                >
                  <MenuItem value="">All</MenuItem>
                  {filterOptions.sponsored.map((v) => (
                    <MenuItem key={v} value={v}>
                      {v}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={3}>
              <TextField
                fullWidth
                size="small"
                label="Categories contains"
                value={filters.categories || ''}
                onChange={(e) => setFilter('categories', e.target.value)}
              />
            </Grid>
            <Grid item xs={6} md={1.5}>
              <TextField
                fullWidth
                size="small"
                type="number"
                label="DA min"
                value={filters.daMin ?? ''}
                onChange={(e) => setFilter('daMin', e.target.value === '' ? '' : Number(e.target.value))}
              />
            </Grid>
            <Grid item xs={6} md={1.5}>
              <TextField
                fullWidth
                size="small"
                type="number"
                label="DA max"
                value={filters.daMax ?? ''}
                onChange={(e) => setFilter('daMax', e.target.value === '' ? '' : Number(e.target.value))}
              />
            </Grid>
            <Grid item xs={6} md={1.5}>
              <TextField
                fullWidth
                size="small"
                type="number"
                label="DR min"
                value={filters.drMin ?? ''}
                onChange={(e) => setFilter('drMin', e.target.value === '' ? '' : Number(e.target.value))}
              />
            </Grid>
            <Grid item xs={6} md={1.5}>
              <TextField
                fullWidth
                size="small"
                type="number"
                label="DR max"
                value={filters.drMax ?? ''}
                onChange={(e) => setFilter('drMax', e.target.value === '' ? '' : Number(e.target.value))}
              />
            </Grid>
            <Grid item xs={6} md={1.5}>
              <TextField
                fullWidth
                size="small"
                type="number"
                label="Traffic min"
                value={filters.trafficMin ?? ''}
                onChange={(e) =>
                  setFilter('trafficMin', e.target.value === '' ? '' : Number(e.target.value))
                }
              />
            </Grid>
            <Grid item xs={6} md={1.5}>
              <TextField
                fullWidth
                size="small"
                type="number"
                label="Traffic max"
                value={filters.trafficMax ?? ''}
                onChange={(e) =>
                  setFilter('trafficMax', e.target.value === '' ? '' : Number(e.target.value))
                }
              />
            </Grid>
            <Grid item xs={6} md={1.5}>
              <TextField
                fullWidth
                size="small"
                type="number"
                label="Price min"
                value={filters.priceMin ?? ''}
                onChange={(e) =>
                  setFilter('priceMin', e.target.value === '' ? '' : Number(e.target.value))
                }
              />
            </Grid>
            <Grid item xs={6} md={1.5}>
              <TextField
                fullWidth
                size="small"
                type="number"
                label="Price max"
                value={filters.priceMax ?? ''}
                onChange={(e) =>
                  setFilter('priceMax', e.target.value === '' ? '' : Number(e.target.value))
                }
              />
            </Grid>
            <Grid item xs={12} md={3} sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
              <Button variant="text" startIcon={<ClearIcon />} onClick={clearFilters}>
                Clear
              </Button>
              <Chip size="small" label={`${total.toLocaleString()} total`} />
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      <Card>
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell padding="checkbox">
                  <Checkbox
                    checked={data.length > 0 && selectedIds.size === data.length}
                    indeterminate={selectedIds.size > 0 && selectedIds.size < data.length}
                    onChange={toggleSelectAll}
                  />
                </TableCell>
                {visibleColumnDefs.map((col) => (
                  <TableCell key={col.key}>{col.label}</TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={visibleColumnDefs.length + 1} align="center" sx={{ py: 6 }}>
                    <CircularProgress size={32} />
                  </TableCell>
                </TableRow>
              ) : data.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={visibleColumnDefs.length + 1} align="center" sx={{ py: 4 }}>
                    <Typography color="text.secondary">
                      No data yet. Import a CSV to get started.
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                data.map((row) => (
                  <TableRow key={row.id} hover selected={selectedIds.has(row.id)}>
                    <TableCell padding="checkbox">
                      <Checkbox
                        checked={selectedIds.has(row.id)}
                        onChange={() => toggleSelect(row.id)}
                      />
                    </TableCell>
                    {visibleColumnDefs.map((col) => (
                      <TableCell key={col.key}>{formatCell(row, col.key)}</TableCell>
                    ))}
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination
          component="div"
          count={total}
          page={page}
          onPageChange={(_e, newPage) => setPage(newPage)}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={(e) => {
            setRowsPerPage(parseInt(e.target.value, 10));
            setPage(0);
          }}
          rowsPerPageOptions={[25, 50, 100, 200]}
        />
      </Card>
      <Dialog
        open={renameOpen}
        onClose={() => !renaming && setRenameOpen(false)}
        fullWidth
        maxWidth="xs"
        disableScrollLock
      >
        <DialogTitle>Rename marketplace</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            fullWidth
            margin="dense"
            label="Marketplace name"
            value={renameName}
            onChange={(e) => setRenameName(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') saveRename();
            }}
            error={!!renameError}
            helperText={renameError || 'Only the name changes. Sites stay in this marketplace.'}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRenameOpen(false)} disabled={renaming}>Cancel</Button>
          <Button variant="contained" onClick={saveRename} disabled={renaming}>
            {renaming ? 'Saving...' : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>
      <Dialog
        open={deleteOpen}
        onClose={() => !deletingMarketplace && setDeleteOpen(false)}
        fullWidth
        maxWidth="xs"
        disableScrollLock
      >
        <DialogTitle>Delete marketplace</DialogTitle>
        <DialogContent>
          <Typography variant="body2">
            Delete <strong>{label}</strong> and all of its sites? This cannot be undone. Other marketplaces are not affected.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteOpen(false)} disabled={deletingMarketplace}>Cancel</Button>
          <Button variant="contained" color="error" onClick={deleteMarketplace} disabled={deletingMarketplace}>
            {deletingMarketplace ? 'Deleting...' : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default MarketplaceData;
